<template>
    <div
        class="ppv2-flex ppv2-flex-wrap ppv2-w-full ppv2-step-wrapper ppv2-pr-2 md:ppv2-gap-[3.125vw]"
        :style="{ paddingLeft: this.normalizeSize(55) }"
    >
        <!-- 📝 FORMULAIRE -->
        <div class="form-section">
            <HeaderStep :step="step" :sizes="sizes" />

            <!-- ⚠️ Message d'erreur serveur -->
            <div
                v-if="error"
                class="ppv2-mb-4 ppv2-px-4 ppv2-py-2 ppv2-rounded-lg ppv2-bg-[#ed4b9e] ppv2-bg-opacity-10 ppv2-border ppv2-border-[#ed4b9e]"
            >
                <span class="error-text">{{ error }}</span>
            </div>

            <!-- 🔥 BADGE DE DÉTECTION DU TYPE -->
            <div v-if="detectedType && showDetectedType" class="detection-badge-container ppv2-mb-4">
                <div
                    class="detection-badge ppv2-flex ppv2-items-center ppv2-gap-2 ppv2-px-3 ppv2-py-1 ppv2-rounded-lg ppv2-border ppv2-mb-7"
                    :class="[
                        hasDetectionAmbiguity
                            ? 'ppv2-bg-orange-50 ppv2-border-orange-400'
                            : 'ppv2-bg-[#31d0aa] ppv2-bg-opacity-10 ppv2-border-[#31d0aa]',
                        'dark:ppv2-bg-opacity-10',
                    ]"
                >
                    <span class="ppv2-text-xl" :class="hasDetectionAmbiguity ? 'ppv2-text-orange-600' : 'ppv2-text-[#31d0aa]'">
                        {{ hasDetectionAmbiguity ? '⚠️' : '✓' }}
                    </span>
                    <div class="ppv2-flex-1">
                        <div
                            class="poppinsMedium ppv2-text-sm"
                            :class="hasDetectionAmbiguity ? 'ppv2-text-orange-800' : 'ppv2-text-[#31d0aa]'"
                        >
                            {{ detectionMessage }}
                        </div>
                        <div v-if="hasDetectionAmbiguity" class="ppv2-text-xs ppv2-text-orange-600 ppv2-mt-1">
                            {{ $t('experience.step3.detection.ambiguityHint') }}
                        </div>
                    </div>
                    <div class="ppv2-text-xl poppinsBold ppv2-text-black ppv2-text-opacity-70">
                        {{ Math.round(detectionInfo.score * 100) }}%
                    </div>
                </div>
            </div>

            <!-- Nom + Nom interne -->
            <div class="ppv2-flex ppv2-gap-4">
                <div class="ppv2-flex-1">
                    <CustomInput
                        :label="$t('experience.step2.labelNom')"
                        :required="true"
                        v-model="experience.name"
                        :placeholder="$t('experience.step2.nom')"
                        :customHeight="40"
                        :state="getState('name')"
                        :errorText="errors.name"
                        @input="clearError('name')"
                        @focus="activeField = 'name'"
                        @blur="activeField = null"
                    />
                </div>
                <div class="ppv2-flex-1">
                    <CustomInput
                        :label="$t('experience.step2.labelInternalName')"
                        :required="false"
                        v-model="experience.internal_name"
                        :placeholder="$t('experience.step2.internalName')"
                        :customHeight="40"
                        :state="getState('internal_name')"
                        :errorText="errors.internal_name"
                        @input="clearError('internal_name')"
                        @focus="activeField = 'internal_name'"
                        @blur="activeField = null"
                    />
                </div>
            </div>

            <!-- Description -->
            <BaseTextarea
                :label="$t('experience.step2.labelDescription')"
                v-model="experience.description"
                :placeholder="$t('experience.step2.description')"
                :useEditor="true"
                @focus="setActiveField('description')"
                @blur="clearActiveField('description')"
                iconIA
                :rows="5"
                :error="errors.description"
                :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                customLabelClass="poppinsLight ppv2-text-black"
                classContainerAdd="ppv2-mb-[20px]"
                :required="false"
            />

            <!-- Upload -->
            <div class="upload-container">
                <CustomInputUpload
                    :label="$t('experience.step2.labelUpload')"
                    v-model="experience.file"
                    :required="false"
                    :showImproveWithAI="false"
                    accept="image/*"
                    @input="onFileSelected"
                    :errorText="errors.file"
                    customClassLabel="poppinsLight ppv2-text-black"
                />
            </div>

            <!-- Boutons -->
            <div class="form-actions" :class="{ '!ppv2-justify-end': complited }">
                <BackButton v-show="!complited" type="button" @click="handleExitClick" />
                <BaseButton id="submit" type="submit" @click="submitForm" variant="primary" :loading="loading">
                    {{ !complited ? $t('experience.next') : $t('common.save') }}
                </BaseButton>
            </div>
        </div>

        <CustomDialog v-model="showExitPopup" :defaultTitle="popupTitle" customClassTitle="poppinsSemiBold" noScroll>
            <template #content>
                <p class="ppv2-text-center ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(15) }">
                    {{ $t('experience.descriptionClosePopup') }}
                </p>
            </template>
            <template #actions>
                <BaseButton
                    id="button"
                    type="button"
                    variant="outline"
                    @click="exit()"
                    customClass="!ppv2-min-h-[40px] !ppv2-text-base !ppv2-min-w-[170px]"
                    :customHeight="43"
                >
                    {{ $t('experience.btnLeave') }}
                </BaseButton>
                <BaseButton
                    type="submit"
                    variant="primary"
                    @click="showExitPopup = false"
                    :customHeight="43"
                    customClass="!ppv2-min-w-[250px] !ppv2-min-h-[40px]"
                >
                    {{ $t('experience.btnStay') }}
                </BaseButton>
            </template>
        </CustomDialog>

        <!-- 👁️ PRÉVISUALISATION -->
        <PreviewExperience :experience="experience" :activeField="activeField" :step="step" />
    </div>
</template>

<script>
import ExperienceMixin from '../mixins/ExperienceMixin'
import CustomInput from '../../../Inputs/CustomInput.vue'
import normalizeSize from '../../../../utils/normalizeSize'
import CustomInputUpload from '../../../Inputs/CustomInputUpload.vue'
import BackButton from '../../../Shared/BackButton.vue'
import HeaderStep from './HeaderStep.vue'
import PreviewExperience from './PreviewExperience.vue'
import BaseButton from '../../../Shared/BaseButton.vue'
import BaseTextarea from '../../../Shared/BaseTextarea.vue'
import CustomDialog from '../../../Shared/CustomDialog.vue'
import CustomButtonSave from '../../../Shared/CustomButtonSave.vue'
import BaseLabel from '../../../Shared/BaseLabel.vue'
import normalizeSizeVertical from '../../../../utils/normalizeSizeVertical'
import { circuitDetectionService, CIRCUIT_TYPES, CONFIDENCE_LEVELS } from '../../../../services/CircuitDetectionService'
import normalizeFontSize from '../../../../utils/normalizeFontSize'
export default {
    components: {
        CustomButtonSave,
        BaseButton,
        BackButton,
        PreviewExperience,
        HeaderStep,
        CustomInputUpload,
        CustomInput,
        BaseTextarea,
        CustomDialog,
        BaseLabel,
    },
    mixins: [ExperienceMixin],
    props: {
        step: { type: Object, required: true },
        sizes: { type: Object, required: true },
        prestation: { type: Object, required: false },
        selected_experience: { type: Object, required: false },
        id: { type: [Number, String], default: null },
        category: {
            type: Object,
            required: false,
        },
        sub_category: {
            type: Object,
            required: false,
        },
        complited: {
            type: Boolean,
            required: false,
        },
    },
    data() {
        return {
            localPrestation: { ...this.prestation }, // make a local copy
            experience: {
                id: 0,
                name: '',
                internal_name: '',
                description: '',
                file: '',
                image: '',
                active: false,
            },
            activeField: null, // ✅ propriété séparée
            editorInstance: null, // ✅ Référence à l'instance TinyMCE
            errors: {},
            error: null, // ✅ Message d'erreur serveur
            showExitPopup: false, // show popop save and exit
            popupTitle: this.$t('experience.titleClosePopup'),
            initialExperience: null,
            // Circuit Detection Service constants
            CIRCUIT_TYPES,
            loading: false,
            CONFIDENCE_LEVELS,
        }
    },
    watch: {
        prestation: {
            handler(newVal) {
                if (newVal) {
                    this.localPrestation = { ...newVal } // update local copy
                    this.initializeExperience(this.localPrestation)
                }
            },
            immediate: true, // Run it once on mount
            deep: true,
        },
    },
    computed: {
        localSelectExperience() {
            return this.selected_experience || {}
        },
        hasId() {
            return this.id !== null && this.id !== undefined
        },
        // 🔥 DÉTECTION AVEC CircuitDetectionService
        detectionInfo() {
            const userSelections = {
                reservation_mode: this.$store.getters['experience/reservationMode'],
                selectedOptionTarifMode: this.$store.getters['experience/selectedOptionTarifMode'],
                isActiveProhibitMixingMinPeople: this.$store.getters['experience/isActiveProhibitMixingMinPeople'],
                prohibitMixingMinPeople: this.$store.getters['experience/prohibitMixingMinPeople'],
            }

            const detection = circuitDetectionService.getBestDetection(userSelections)

            if (!detection.success) {
                return {
                    type: null,
                    score: 0,
                    confidence: CONFIDENCE_LEVELS.LOW,
                    isAmbiguous: false,
                }
            }

            return {
                type: detection.type,
                score: detection.score / 100,
                confidence: detection.confidence,
                isAmbiguous: detection.isAmbiguous,
                allScores: detection.allScores,
            }
        },
        detectedType() {
            const info = this.detectionInfo
            return info.confidence === CONFIDENCE_LEVELS.EXACT || info.confidence === CONFIDENCE_LEVELS.HIGH ? info.type : null
        },
        // ✅ Control visibility of detected type block from store
        showDetectedType() {
            return this.$store.getters['experience/showDetectedType']
        },
        detectionMessage() {
            const info = this.detectionInfo
            if (!info.type) {
                return this.$t('experience.step3.detection.waitingConfig')
            }

            const typeName = circuitDetectionService.getTypeName(info.type)

            if (info.isAmbiguous) {
                return this.$t('experience.step3.detection.detectedTypeAmbiguous', { typeName })
            }

            return this.$t('experience.step3.detection.detectedType', { typeName })
        },
        hasDetectionAmbiguity() {
            return this.detectionInfo.isAmbiguous || false
        },
    },
    methods: {
        normalizeFontSize,
        normalizeSizeVertical,
        normalizeSize,
        clearError(field) {
            if (this.errors[field]) {
                this.$delete(this.errors, field)
            }
        },
        onFileSelected(file) {
            if (!file) return

            const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif']
            if (!allowedTypes.includes(file.type)) {
                this.$set(this.errors, 'file', this.$t('experience.step2.errors.imageOnly'))
                this.experience.file = ''
                return
            }

            this.$delete(this.errors, 'file')
            const reader = new FileReader()
            reader.onload = () => {
                this.experience.file = reader.result // Base64 string
            }
            reader.readAsDataURL(file)
        },
        setActiveField(field) {
            console.log(`🎯 Focus sur le champ: ${field}`)
            this.activeField = field
        },
        clearActiveField(field) {
            // Vérifie si le focus est toujours dans Ckeditor
            if (field === 'description' && this.editorInstance && this.editorInstance.hasFocus()) {
                console.log('🔘 Ckeditor a toujours le focus, on ne change pas')
                return
            }

            if (this.activeField === field) {
                console.log(`🔘 Blur du champ: ${field}`)
                this.activeField = null
            }
        },

        async submitForm() {
            // ✅ FIX: Reset ALL errors (clears stale backend keys from previous 422)
            this.errors = {}
            this.error = null

            // Validate name
            if (!this.experience.name) {
                this.$set(this.errors, 'name', this.$t('experience.errors.required'))
            } else {
                this.$delete(this.errors, 'name')
            }

            if (Object.keys(this.errors).length === 0) {
                console.log('✅ Formulaire valide :', this.experience)
                // ✅ FIX: Attendre le résultat et seulement naviguer si succès
                const success = await this.saveExperience()
                if (success) {
                    this.$toast.success(this.$t('experience.saved'))
                    if (!this.complited) {
                        this.$emit('next-step')
                    }
                } else {
                    console.error('❌ Impossible de passer au step suivant - erreur de sauvegarde')
                }
            }
        },
        getState(field) {
            if (this.errors[field]) return 'error'
            if (this.experience[field]) return 'valid'
            return 'default'
        },
        initializeExperience(prestation) {
            if (!prestation || Object.keys(prestation).length === 0) {
                this.initialExperience = JSON.parse(JSON.stringify(this.experience))
                return
            }
            this.experience = {
                id: prestation.id || 0,
                name: prestation.name || '',
                internal_name: prestation.internal_name || '',
                description: prestation.description || '',
                active: prestation.active || 0,
                file: prestation.image || '',
                image: prestation.image,
            }

            this.initialExperience = JSON.parse(JSON.stringify(this.experience))
        },
        async saveAndExit() {
            this.$emit('save-and-exit')
        },
        async saveExperience(redirect) {
            this.loading = true
            this.error = null

            try {
                // Primary detected type uses exact matching (not scoring) for backend accuracy
                const primaryDetectedType = this.$store.getters['experience/getPrimaryDetectedType']
                const userSelections = this.$store.getters['experience/userSelections'] ?? {}

                const formData = {
                    ...this.experience,
                    step: this.step.id,
                    selectExperience: this.localSelectExperience,
                    detected_type: primaryDetectedType,
                    detection_score: primaryDetectedType ? 1.0 : null,
                    detection_confidence: primaryDetectedType ? 'exact' : null,
                    user_selections: userSelections,
                }

                const object = await axios.post('/api/v3/experience/save_exit_step_two', formData)
                this.$emit('update-prestation', object.data.data)
                if (redirect) {
                    const baseUrl = `${window.location.protocol}//${window.location.host}`
                    window.location.href = `${baseUrl}/back-office/v3/experience`
                } else {
                    if (!this.hasId) {
                        const newUrl = `/back-office/v3/experience/create/${object.data.data.id}/experience`
                        window.history.pushState({ prestationId: object.data.data.id }, '', newUrl)
                    }
                }
                return true
            } catch (err) {
                console.error('❌ Step2 - Error saving experience:', {
                    status: err.response?.status,
                    data: err.response?.data,
                    message: err.message,
                })
                this.error = err.response?.data?.message || this.$t('experience.errors.save')
                this.$set(this.errors, 'server', this.error)
                return false
            } finally {
                this.loading = false
            }
        },
        exit() {
            const baseUrl = `${window.location.protocol}//${window.location.host}`
            window.location.href = `${baseUrl}/back-office/v3/experience`
        },
        hasChanges() {
            return JSON.stringify(this.experience) !== JSON.stringify(this.initialExperience)
        },
        async handleExitClick() {
            if (this.hasChanges()) {
                this.showExitPopup = true
            } else {
                this.$emit('previous-step')
            }
        },
    },
}
</script>

<style scoped>
.upload-container {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-bottom: 20px;
    opacity: 0.7;
}
.upload-container:hover,
.upload-container:focus,
.upload-container:active {
    opacity: 1;
}

/* Partie gauche */
.form-section {
    flex: 1;
}
</style>
